<template>
  <v-dialog
    :value="showModal"
    content-class="default-property-settings-modal"
  >
    <v-btn
      icon
      class="close-modal-btn"
      @click="showModal = !showModal"
    >
      <v-icon size="20">
        $faTimes
      </v-icon>
    </v-btn>
    <h2
      class="mb-8"
      style="font-weight: 400"
    >
      Managed Property Settings
    </h2>
    <h3 class="mb-4">
      Alert Settings for New Properties
    </h3>
    <div
      v-for="(item, name) in newSettings"
      :key="name"
      class="toggle-container"
    >
      <setting-tooltip :enabled="!settingsEnabled">
        <template v-slot:activator="{ on, attrs }">
          <div
            v-bind="attrs"
            v-on="on"
          >
            <toggle
              :value="item.value"
              :indeterminate="false"
              :disabled="!settingsEnabled"
              @toggle="value => handleNotificationSettingsChange({updated: name, value: value || false})"
            >
              <template #label>
                <div class="default-property-settings-toggle">
                  <h6>{{ item.label }}</h6>
                  <div>{{ item.description }}</div>
                </div>
              </template>
            </toggle>
          </div>
        </template>
        <span>
          This field cannot be edited because this user belongs to multiple organizations
        </span>
      </setting-tooltip>
    </div>
    <template #activator="{on, attrs}">
      <v-btn
        v-bind="attrs"
        color="primary"
        class="my-3 icon-btn"
        v-on="on"
        @click="showModal = true"
      >
        <v-icon
          size="16"
          class="mr-2"
        >
          $faCog
        </v-icon>
        Property Settings
      </v-btn>
    </template>
    <v-divider class="my-3" />
    <h3 class="mb-4">
      Report Settings for New Properties
    </h3>
    <toggle
      :value="addNewProperty"
      @toggle="value => updateUserReportPreference(value)"
    >
      <template #label>
        <div class="default-property-settings-toggle">
          <h6>Daily ResolutionReports</h6>
          <div>
            New properties added to this account will be automatically
            included in your ResolutionReports.
          </div>
        </div>
      </template>
    </toggle>
    <div class="daily-digest-note">
      <i>Note: This option does <b>not</b> affect existing properties.</i>
    </div>
  </v-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { OCCUPANCY } from '@/helpers/feature-constants.js'
  import Toggle from '@/components/base/Toggle'
  import SettingTooltip from '@/components/user/SettingTooltip'
  import axios from 'axios'
  import { getTimeZones } from '@vvo/tzdb'

  export default {
    name: 'DefaultPropertySettings',
    components: { Toggle, SettingTooltip },
    data () {
      return {
        newSettings: {},
        showModal: false,
        inStayDefault: false,
        maintenanceDefault: false,
        addNewProperty: false,
        needToInitUserPreference: false,
      }
    },
    computed: {
      ...mapGetters({
        savedUserData: 'Users/userData',
        eligibleFeatures: 'Organizations/eligibleFeatures',
        currentUser: 'Users/currentUser',
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        canAdminUser: 'Permissions/canAdminUser',
        isUserSelf: 'Permissions/isUserSelf',
        isInternal: 'Permissions/isInternal',
        notificationSettings: 'Users/userNotificationSettings',
        savedUserTimezone: 'Users/userTimezone',
      }),
      orgId () {
        return this.currentSelectedOrg?.id
      },
      settingsEnabled () {
        const isSingleOrg = this.savedUserData.orgs?.length === 1
        return this.isUserSelf ||
          (this.isInternal && this.orgId) ||
          (this.canAdminUser(this.savedUserData, this.orgId) && isSingleOrg)
      },
      occupancyEligible () {
        return this.eligibleFeatures.includes(OCCUPANCY)
      },
      defaultTimezoneFromClientOffset () {
        const offset = -(new Date()).getTimezoneOffset()
        let timezone = getTimeZones().filter(tz => tz.currentTimeOffsetInMinutes === offset)[0] || 'America/Los_Angeles'
        try {
          timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        } catch (e) {
          // Intl API not supported
        } return timezone
      },
    },
    watch: {
      notificationSettings () {
        this.setNotifications()
      },
    },
    async mounted () {
      this.setNotifications()
      await this.fetchReportSettings()
    },
    methods: {
      async fetchReportSettings () {
        const res = await axios.get(`${this.$store.state.baseURL}/reports/user/${this.savedUserData.id}/preference`)
        if (res.data.data) {
          const { inStayDefault, maintenanceDefault, addNewProperty } = res.data.data
          this.inStayDefault = inStayDefault
          this.maintenanceDefault = maintenanceDefault
          this.addNewProperty = addNewProperty
        } else this.needToInitUserPreference = true
      },
      handleNotificationSettingsChange ({ updated, value }) {
        this.$store.dispatch('Users/updateUserNotificationSettings', {
          updated,
          value,
          userId: this.savedUserData.id,
          orgId: this.savedUserData.orgs[0].id,
        })
      },
      setNotifications () {
        this.newSettings = {
          addActionableAlertNotifications: {
            label: 'Noise Alerts',
            value: this.notificationSettings.addActionableAlertNotifications,
            description: 'Turn noise alerts on for any new properties added to this account.',
          },
          addDiscoNotifications: {
            label: 'Disconnect Alerts',
            value: this.notificationSettings.addDiscoNotifications,
            description: 'Turn disconnect alerts on for any new properties added to this account.',
          },
          addOccupancyNotifications: {
            label: 'CrowdControl Alerts',
            value: this.notificationSettings.addOccupancyNotifications,
            description: 'Turn occupancy alerts on for any new properties added to this account.',
          },
        }
      },
      async updateUserReportPreference (preference) {
        const payload = {
          inStayDefault: preference,
          addNewProperty: preference,
          UserID: this.savedUserData.id,
          timeZone: this.savedUserTimezone || this.defaultTimezoneFromClientOffset,
          orgs: this.savedUserData.orgs?.map(org => org.id),
        }
        if (this.needToInitUserPreference) {
          const res = await axios.post(`${this.$store.state.baseURL}/reports/user/preference`, payload)
          this.inStayDefault = res.data.data.inStayDefault
          this.addNewProperty = res.data.data.addNewProperty
          this.needToInitUserPreference = false
        } else {
          const res = await axios.put(`${this.$store.state.baseURL}/reports/user/${this.savedUserData.id}/preference`, payload)
          this.inStayDefault = res.data.data.inStayDefault
          this.addNewProperty = res.data.data.addNewProperty
        }
      },
    },
  }
</script>

<style lang="scss">
.default-property-settings-modal {
  width: 676px;
  background: #F1EDE6;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 52px 57px;
  position: relative;
  .toggle-container {
    display: flex;
    flex-direction: column;
    align-content: flex-start;
    .v-input--selection-controls {
      margin: 0 0 12px 0 !important
    }
  }
  .close-modal-btn {
    position: absolute;
    right: 50px;
    top: 50px;
  }
  .default-property-settings-toggle {
    color: $base;
    & h6 {
      margin-top: -8px;
    }
  }
  .daily-digest-note {
    color: $base;
    padding-left: 65px;
    padding-top: 16px;
  }
}
</style>
